import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sun, Building, Users, BarChart3, LogOut, ArrowRight, Megaphone,
  Power, Key, Plus, Search, RefreshCw, TrendingUp,
  AlertTriangle, Info, Wrench, Clock, ChevronDown, ChevronRight
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = {
  trial: { label: "Trial", color: "bg-blue-100 text-blue-700" },
  starter: { label: "Starter", color: "bg-purple-100 text-purple-700" },
  growth: { label: "Growth", color: "bg-amber-100 text-amber-700" },
  enterprise: { label: "Scale", color: "bg-emerald-100 text-emerald-700" },
  lifetime: { label: "Lifetime", color: "bg-pink-100 text-pink-700" },
};
const STATUS_COLORS = {
  trial: "bg-blue-100 text-blue-700",
  active: "bg-emerald-100 text-emerald-700",
  suspended: "bg-red-100 text-red-700",
  expired: "bg-slate-100 text-slate-600",
};
const ANN_TYPES = {
  info: { label: "Info", color: "bg-blue-50 border-blue-200 text-blue-800", icon: Info },
  warning: { label: "Warning", color: "bg-amber-50 border-amber-300 text-amber-900", icon: AlertTriangle },
  maintenance: { label: "Maintenance", color: "bg-red-50 border-red-200 text-red-800", icon: Wrench },
};

export default function SuperAdminPage() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [companies, setCompanies] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [credForm, setCredForm] = useState({ current_password: "", new_email: "", new_password: "" });
  const [credSaving, setCredSaving] = useState(false);

  const changeCredentials = async () => {
    if (!credForm.current_password) { toast.error("Enter your current password"); return; }
    if (!credForm.new_email && !credForm.new_password) { toast.error("Provide a new email and/or new password"); return; }
    setCredSaving(true);
    try {
      const { data } = await axios.post(`${API}/superadmin/change-credentials`, credForm);
      toast.success(data.message || "Credentials updated");
      setCredForm({ current_password: "", new_email: "", new_password: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update credentials");
    }
    setCredSaving(false);
  };
  const [userSearch, setUserSearch] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const [annForm, setAnnForm] = useState({ title: "", message: "", type: "info", expires_at: "" });
  const [showAnnForm, setShowAnnForm] = useState(false);
  const [extendModal, setExtendModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);      // { id, name } — permanent delete, guarded by typing the name
  const [deleteTyped, setDeleteTyped] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [extendDays, setExtendDays] = useState(7);
  const [extendDate, setExtendDate] = useState("");
  const [resetCreds, setResetCreds] = useState(null);
  const [broadcastMsg, setBroadcastMsg] = useState({ title: "", message: "" });

  const isAuthed = user?.role === "super_admin";

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const data = await login(loginForm.email, loginForm.password);
      if (data.user.role !== "super_admin") { logout(); toast.error("Access denied. Super Admin only."); }
      else { toast.success("Logged in as Super Admin"); }
    } catch (err) { toast.error(err.response?.data?.detail || "Login failed"); }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, aRes, uRes, annRes] = await Promise.all([
        axios.get(`${API}/superadmin/companies`),
        axios.get(`${API}/superadmin/analytics`),
        axios.get(`${API}/superadmin/users`),
        axios.get(`${API}/superadmin/announcements`),
      ]);
      setCompanies(cRes.data);
      setAnalytics(aRes.data);
      setAllUsers(uRes.data);
      setAnnouncements(annRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { if (isAuthed) fetchAll(); }, [isAuthed, fetchAll]);

  const toggleCompany = async (id, current) => {
    try {
      await axios.put(`${API}/superadmin/companies/${id}/toggle`);
      setCompanies(p => p.map(c => c.id === id ? { ...c, is_active: !c.is_active, subscription_status: !c.is_active ? c.subscription_status : "suspended" } : c));
      toast.success(current ? "Company suspended" : "Company reactivated");
    } catch (err) { console.error(err); }
  };

  const updateSubscription = async (id, plan, status) => {
    try {
      await axios.put(`${API}/superadmin/companies/${id}/subscription`, { plan, status });
      setCompanies(p => p.map(c => c.id === id ? { ...c, subscription_plan: plan, subscription_status: status } : c));
      toast.success("Subscription updated");
    } catch (err) { console.error(err); }
  };

  const extendTrial = async () => {
    if (!extendModal) return;
    try {
      const payload = extendDate ? { trial_end_date: extendDate } : { days: extendDays };
      const { data } = await axios.post(`${API}/superadmin/companies/${extendModal.id}/extend-trial`, payload);
      const daysLeft = data.trial_end_date ? Math.max(0, Math.ceil((new Date(data.trial_end_date) - new Date()) / 86400000)) : extendDays;
      setCompanies(p => p.map(c => c.id === extendModal.id ? { ...c, trial_end_date: data.trial_end_date, trial_days_left: daysLeft } : c));
      toast.success(extendDate ? `Trial end date set to ${new Date(data.trial_end_date).toLocaleDateString("en-IN")}` : `Trial extended by ${extendDays} days`);
      setExtendModal(null);
      setExtendDate("");
    } catch (err) { console.error(err); toast.error(err.response?.data?.detail || "Could not update trial"); }
  };

  const deleteWorkspace = async () => {
    if (!deleteModal || deleteTyped.trim() !== deleteModal.name.trim()) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/superadmin/companies/${deleteModal.id}`);
      setCompanies(p => p.filter(c => c.id !== deleteModal.id));
      toast.success(`${deleteModal.name} deleted permanently`);
      setDeleteModal(null); setDeleteTyped("");
    } catch (err) { console.error(err); toast.error(err.response?.data?.detail || "Could not delete workspace"); }
    finally { setDeleting(false); }
  };

  const impersonate = async (userId) => {
    try {
      await axios.post(`${API}/superadmin/impersonate/${userId}`);
      toast.success("Entering workspace as user...");
      setTimeout(() => { window.location.href = "/app/dashboard"; }, 800);
    } catch (err) { console.error(err); }
  };

  const impersonateCompany = async (company) => {
    const admin = allUsers.find(u => u.workspace_id === company.id && u.role === "company_admin");
    if (admin) impersonate(admin.id);
    else toast.error("No Company Admin found for this workspace");
  };

  const toggleUser = async (userId, current) => {
    try {
      await axios.put(`${API}/superadmin/users/${userId}/toggle`);
      setAllUsers(p => p.map(u => u.id === userId ? { ...u, is_active: !current } : u));
      toast.success(current ? "User deactivated" : "User reactivated");
    } catch (err) { console.error(err); }
  };

  const forceReset = async (userId) => {
    try {
      const { data } = await axios.post(`${API}/superadmin/users/${userId}/force-reset`);
      setResetCreds(data);
      toast.success("Password reset");
    } catch (err) { console.error(err); }
  };

  const createAnnouncement = async (e) => {
    e.preventDefault();
    if (!annForm.title || !annForm.message) { toast.error("Fill title and message"); return; }
    try {
      const { data } = await axios.post(`${API}/superadmin/announcements`, annForm);
      setAnnouncements(p => [data, ...p]);
      setAnnForm({ title: "", message: "", type: "info", expires_at: "" });
      setShowAnnForm(false);
      toast.success("Announcement published!");
    } catch (err) { console.error(err); }
  };

  const deactivateAnnouncement = async (id) => {
    try {
      await axios.delete(`${API}/superadmin/announcements/${id}`);
      setAnnouncements(p => p.map(a => a.id === id ? { ...a, is_active: false } : a));
      toast.success("Announcement deactivated");
    } catch (err) { console.error(err); }
  };

  const sendBroadcast = async () => {
    if (!broadcastMsg.title || !broadcastMsg.message) { toast.error("Fill title and message"); return; }
    try {
      const { data } = await axios.post(`${API}/superadmin/broadcast`, broadcastMsg);
      toast.success(`Notification sent to ${data.sent_to} users`);
      setBroadcastMsg({ title: "", message: "" });
    } catch (err) { console.error(err); }
  };

  const searchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/superadmin/users?search=${encodeURIComponent(userSearch)}`);
      setAllUsers(data);
    } catch (err) { console.error(err); }
  };

  const filteredCompanies = useMemo(() => companies.filter(c =>
    !companySearch || c.name.toLowerCase().includes(companySearch.toLowerCase()) || c.slug?.includes(companySearch.toLowerCase())
  ), [companies, companySearch]);

  const TABS = [
    { key: "overview", label: "Overview", icon: BarChart3 },
    { key: "tenants", label: "Tenants", icon: Building },
    { key: "users", label: "Users", icon: Users },
    { key: "analytics", label: "Analytics", icon: TrendingUp },
    { key: "announcements", label: "Announcements", icon: Megaphone },
    { key: "security", label: "Security", icon: Key },
  ];

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-[#F5F8F5] flex items-center justify-center font-body">
        <div className="bg-white rounded-lg border border-[#1A2238] p-8 w-full max-w-sm shadow-xl">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-9 h-9 bg-[#FFB703] rounded-lg flex items-center justify-center">
              <Sun size={18} className="text-[#0A1128]" />
            </div>
            <div>
              <div className="font-heading font-bold text-[#0A1128] text-lg">SunBiz</div>
              <div className="text-xs text-[#64748B]">Super Admin Console</div>
            </div>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Admin Email</label>
              <input type="email" required value={loginForm.email} onChange={e => setLoginForm(p => ({ ...p, email: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" data-testid="superadmin-email" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Password</label>
              <input type="password" required value={loginForm.password} onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" data-testid="superadmin-password" />
            </div>
            <button type="submit" className="w-full bg-[#FFB703] text-[#0A1128] font-bold py-3 rounded text-sm hover:bg-[#E8A200] transition-colors flex items-center justify-center gap-2" data-testid="superadmin-login-btn">
              Access Admin Console <ArrowRight size={14} />
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1F5F9] font-body">
      {/* Header */}
      <div className="bg-[#F5F8F5] px-6 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 bg-[#FFB703] rounded flex items-center justify-center">
            <Sun size={14} className="text-[#0A1128]" />
          </div>
          <span className="font-heading font-bold text-[#1B2B22] text-sm">SunBiz</span>
          <span className="text-[#475569] text-xs">/ Super Admin Console</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={fetchAll} className="p-1.5 rounded text-[#64748B] hover:text-[#1B2B22] transition-colors" title="Refresh data">
            <RefreshCw size={14} />
          </button>
          <button onClick={() => { logout(); navigate("/superadmin"); }} className="flex items-center gap-2 text-xs text-[#64748B] hover:text-[#1B2B22] transition-colors">
            <LogOut size={14} />Sign Out
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tab Navigation */}
        <div className="flex gap-1 bg-white border border-[#E2E8F0] rounded-lg p-1 mb-6 w-fit overflow-x-auto">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 text-xs rounded-md font-semibold transition-all whitespace-nowrap ${activeTab === t.key ? "bg-[#F5F8F5] text-[#1B2B22] shadow" : "text-[#64748B] hover:text-[#0A1128]"}`}
              data-testid={`sa-tab-${t.key}`}>
              <t.icon size={13} />{t.label}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW TAB ───────────────────────────────────────── */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Top metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { label: "Total Companies", value: analytics?.total_companies || 0, color: "text-[#0A1128]" },
                { label: "Paid", value: analytics?.paid_companies || 0, color: "text-emerald-600" },
                { label: "On Trial", value: analytics?.trial_companies || 0, color: "text-blue-600" },
                { label: "Suspended", value: analytics?.suspended_companies || 0, color: "text-red-600" },
                { label: "Trial Expiring (3d)", value: analytics?.expiring_soon || 0, color: "text-amber-600" },
                { label: "New This Month", value: analytics?.new_this_month || 0, color: "text-purple-600" },
              ].map(card => (
                <div key={card.label} className="bg-white border border-[#E2E8F0] rounded-lg p-4">
                  <div className="text-[9px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-1">{card.label}</div>
                  <div className={`font-heading font-bold text-2xl ${card.color}`}>{card.value}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: "Total Users", value: analytics?.total_users || 0 },
                { label: "Total Projects", value: analytics?.total_projects || 0 },
                { label: "DPRs Logged", value: analytics?.total_dprs || 0 },
                { label: "Revenue (Manual PIs)", value: `₹${((analytics?.total_received_inr || 0) / 100000).toFixed(1)}L` },
              ].map(card => (
                <div key={card.label} className="bg-white border border-[#E2E8F0] rounded-lg p-4">
                  <div className="text-[9px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-1">{card.label}</div>
                  <div className="font-heading font-bold text-xl text-[#0A1128]">{card.value}</div>
                </div>
              ))}
            </div>

            {/* Feature Adoption */}
            <div className="bg-white border border-[#E2E8F0] rounded-lg p-5">
              <h3 className="font-heading font-semibold text-sm text-[#0A1128] mb-4">Feature Adoption</h3>
              <div className="space-y-3">
                {[
                  { label: "Using Financial Docs (PO/PI)", value: analytics?.ws_with_financials || 0, total: analytics?.total_companies || 1 },
                  { label: "Logging DPRs", value: analytics?.ws_with_dpr || 0, total: analytics?.total_companies || 1 },
                  { label: "Document Uploads", value: analytics?.ws_with_documents || 0, total: analytics?.total_companies || 1 },
                ].map(row => {
                  const pct = Math.round((row.value / row.total) * 100);
                  return (
                    <div key={row.label}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-[#64748B]">{row.label}</span>
                        <span className="font-semibold text-[#0A1128]">{row.value} / {row.total} companies ({pct}%)</span>
                      </div>
                      <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                        <div className="h-full bg-[#FFB703] rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Task completion */}
            <div className="bg-white border border-[#E2E8F0] rounded-lg p-5">
              <h3 className="font-heading font-semibold text-sm text-[#0A1128] mb-4">Platform Activity</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                {[
                  { label: "Total Tasks", value: analytics?.total_tasks || 0 },
                  { label: "Completed Tasks", value: analytics?.completed_tasks || 0 },
                  { label: "Purchase Orders", value: analytics?.total_pos || 0 },
                  { label: "Proforma Invoices", value: analytics?.total_pis || 0 },
                ].map(item => (
                  <div key={item.label} className="p-3 bg-[#F8FAFC] rounded">
                    <div className="font-bold text-xl text-[#0A1128]">{item.value}</div>
                    <div className="text-[10px] text-[#64748B] mt-1">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── TENANTS TAB ────────────────────────────────────────── */}
        {activeTab === "tenants" && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1 max-w-xs">
                <Search size={13} className="absolute left-3 top-2.5 text-[#94A3B8]" />
                <input type="text" value={companySearch} onChange={e => setCompanySearch(e.target.value)} placeholder="Search company..." className="w-full pl-9 pr-3 py-2 text-sm border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <span className="text-xs text-[#64748B]">{filteredCompanies.length} companies</span>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                      {["Company", "Plan", "Status", "Trial Left", "Projects", "Users", "DPRs", "Last Activity", "Actions"].map(h => (
                        <th key={h} className="text-left px-4 py-2.5 text-[10px] font-semibold text-[#64748B] uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCompanies.map(c => (
                      <tr key={c.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`company-row-${c.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-[#0A1128] text-xs">{c.name}</div>
                          <div className="text-[10px] text-[#94A3B8]">/{c.slug}</div>
                        </td>
                        <td className="px-4 py-3">
                          <select value={c.subscription_plan || "trial"} onChange={e => updateSubscription(c.id, e.target.value, c.subscription_status)}
                            className={`text-[10px] font-bold uppercase px-2 py-1 rounded border-0 cursor-pointer ${(PLANS[c.subscription_plan] || PLANS.trial).color}`}>
                            {Object.keys(PLANS).map(p => <option key={p} value={p}>{PLANS[p].label}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <select value={c.subscription_status || "trial"} onChange={e => updateSubscription(c.id, c.subscription_plan, e.target.value)}
                            className={`text-[10px] font-bold uppercase px-2 py-1 rounded border-0 cursor-pointer ${STATUS_COLORS[c.subscription_status] || STATUS_COLORS.trial}`}>
                            {["trial", "active", "suspended", "expired"].map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-3 text-xs tabular-nums">
                          {c.trial_days_left !== null && c.trial_days_left !== undefined ? (
                            <span className={`font-semibold ${c.trial_days_left <= 3 ? "text-red-600" : c.trial_days_left <= 7 ? "text-amber-600" : "text-[#64748B]"}`}>
                              {c.trial_days_left}d
                            </span>
                          ) : "—"}
                        </td>
                        <td className="px-4 py-3 text-xs tabular-nums">{c.project_count}</td>
                        <td className="px-4 py-3 text-xs tabular-nums">{c.user_count}</td>
                        <td className="px-4 py-3 text-xs tabular-nums">{c.dpr_count}</td>
                        <td className="px-4 py-3 text-[10px] text-[#94A3B8]">
                          {c.last_activity ? new Date(c.last_activity).toLocaleDateString("en-IN") : "Never"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => { setExtendModal(c); setExtendDays(7); setExtendDate(""); }} className="p-1.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors" title="Extend Trial" data-testid={`extend-trial-${c.id}`}>
                              <Clock size={12} />
                            </button>
                            <button onClick={() => impersonateCompany(c)} className="p-1.5 rounded bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors" title="Enter as Admin" data-testid={`impersonate-${c.id}`}>
                              <ArrowRight size={12} />
                            </button>
                            <button onClick={() => toggleCompany(c.id, c.is_active)} className={`p-1.5 rounded transition-colors ${c.is_active !== false ? "bg-emerald-50 text-emerald-600 hover:bg-red-50 hover:text-red-600" : "bg-red-50 text-red-600 hover:bg-emerald-50 hover:text-emerald-600"}`} title={c.is_active !== false ? "Suspend" : "Reactivate"} data-testid={`toggle-company-${c.id}`}>
                              <Power size={12} />
                            </button>
                          </div>
                          {/* destructive action kept away from the icon row: a muted text link that opens a typed-name confirmation */}
                          <button onClick={() => { setDeleteModal({ id: c.id, name: c.name }); setDeleteTyped(""); }} className="ml-3 text-[10px] text-slate-400 hover:text-red-600 underline-offset-2 hover:underline" data-testid={`delete-company-${c.id}`}>Delete…</button>
                        </td>
                      </tr>
                    ))}
                    {filteredCompanies.length === 0 && (
                      <tr><td colSpan={9} className="px-4 py-12 text-center text-[#64748B] text-sm">No companies found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── USERS TAB ──────────────────────────────────────────── */}
        {activeTab === "users" && (
          <div>
            <div className="flex gap-2 mb-4">
              <div className="relative flex-1 max-w-md">
                <Search size={13} className="absolute left-3 top-2.5 text-[#94A3B8]" />
                <input type="text" value={userSearch} onChange={e => setUserSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && searchUsers()} placeholder="Search by name or email... (Enter to search)" className="w-full pl-9 pr-3 py-2 text-sm border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#FFB703]" data-testid="user-search-input" />
              </div>
              <button onClick={searchUsers} className="px-4 py-2 bg-[#FFB703] text-[#0A1128] font-semibold rounded text-sm hover:bg-[#E8A200] transition-colors" data-testid="user-search-btn">Search</button>
              <button onClick={() => { setUserSearch(""); fetchAll(); }} className="px-3 py-2 border border-[#E2E8F0] rounded text-sm text-[#64748B] hover:bg-slate-50"><RefreshCw size={13} /></button>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                      {["Name", "Email", "Company", "Role", "Status", "Actions"].map(h => (
                        <th key={h} className="text-left px-4 py-2.5 text-[10px] font-semibold text-[#64748B] uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers.map(u => (
                      <tr key={u.id} className={`border-b border-[#F1F5F9] hover:bg-[#F8FAFC] ${!u.is_active ? "opacity-60" : ""}`} data-testid={`user-row-${u.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-[#FEF3C7] flex items-center justify-center text-[#0A1128] font-bold text-xs flex-shrink-0">
                              {u.full_name?.charAt(0)?.toUpperCase()}
                            </div>
                            <span className="text-xs font-medium text-[#0A1128]">{u.full_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#64748B]">{u.email}</td>
                        <td className="px-4 py-3 text-xs text-[#64748B]">{u.workspace_name}</td>
                        <td className="px-4 py-3">
                          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{u.role}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${u.is_active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                            {u.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => forceReset(u.id)} className="p-1.5 rounded bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors" title="Force Reset Password" data-testid={`reset-user-${u.id}`}>
                              <Key size={12} />
                            </button>
                            <button onClick={() => toggleUser(u.id, u.is_active)} className={`p-1.5 rounded transition-colors ${u.is_active ? "bg-red-50 text-red-500 hover:bg-red-100" : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"}`} title={u.is_active ? "Deactivate" : "Reactivate"} data-testid={`toggle-user-${u.id}`}>
                              <Power size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {allUsers.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-12 text-center text-[#64748B] text-sm">No users found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── ANALYTICS TAB ──────────────────────────────────────── */}
        {activeTab === "analytics" && (
          <div className="space-y-4">
            <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
              <div className="px-5 py-3 border-b border-[#E2E8F0]">
                <h3 className="font-heading font-semibold text-sm text-[#0A1128]">Per-Workspace Usage Breakdown</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                      {["Company", "Plan", "Projects", "Users", "DPRs Logged", "Trial Ends", "Engagement"].map(h => (
                        <th key={h} className="text-left px-4 py-2.5 text-[10px] font-semibold text-[#64748B] uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...companies].sort((a, b) => (b.dpr_count + b.project_count) - (a.dpr_count + a.project_count)).map(c => {
                      const score = Math.min(100, Math.round(((c.dpr_count * 3 + c.project_count * 5 + c.user_count * 2) / 50) * 100));
                      return (
                        <tr key={c.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]">
                          <td className="px-4 py-3 font-medium text-[#0A1128] text-xs">{c.name}</td>
                          <td className="px-4 py-3">
                            <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${(PLANS[c.subscription_plan] || PLANS.trial).color}`}>
                              {(PLANS[c.subscription_plan] || PLANS.trial).label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs tabular-nums font-medium">{c.project_count}</td>
                          <td className="px-4 py-3 text-xs tabular-nums">{c.user_count}</td>
                          <td className="px-4 py-3 text-xs tabular-nums font-medium text-[#059669]">{c.dpr_count}</td>
                          <td className="px-4 py-3 text-[10px] text-[#64748B]">
                            {c.trial_end_date ? new Date(c.trial_end_date).toLocaleDateString("en-IN") : "—"}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 bg-[#F1F5F9] rounded-full w-16">
                                <div className="h-full rounded-full bg-[#FFB703]" style={{ width: `${score}%` }} />
                              </div>
                              <span className="text-[10px] font-semibold text-[#64748B]">{score}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── ANNOUNCEMENTS TAB ──────────────────────────────────── */}
        {activeTab === "announcements" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <button onClick={() => setShowAnnForm(true)} className="flex items-center gap-2 bg-[#FFB703] text-[#0A1128] font-semibold px-4 py-2 rounded text-sm hover:bg-[#E8A200] transition-colors" data-testid="create-announcement-btn">
                <Plus size={14} />New Announcement
              </button>
              <span className="text-xs text-[#64748B]">Announcements appear as a banner for all logged-in users</span>
            </div>

            {/* Active Announcements */}
            <div>
              <h3 className="text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-2">Active</h3>
              {announcements.filter(a => a.is_active).length === 0 ? (
                <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 text-center text-sm text-[#64748B]">No active announcements</div>
              ) : announcements.filter(a => a.is_active).map(a => {
                const t = ANN_TYPES[a.type] || ANN_TYPES.info;
                return (
                  <div key={a.id} className={`border rounded-lg p-4 mb-2 ${t.color} flex items-start justify-between gap-3`} data-testid={`announcement-${a.id}`}>
                    <div className="flex items-start gap-2">
                      <t.icon size={14} className="mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-semibold text-sm">{a.title}</div>
                        <div className="text-xs mt-0.5 opacity-80">{a.message}</div>
                        <div className="text-[10px] mt-1 opacity-60">
                          Created {new Date(a.created_at).toLocaleDateString("en-IN")}
                          {a.expires_at ? ` · Expires ${new Date(a.expires_at).toLocaleDateString("en-IN")}` : " · No expiry"}
                        </div>
                      </div>
                    </div>
                    <button onClick={() => deactivateAnnouncement(a.id)} className="text-xs opacity-60 hover:opacity-100 font-medium hover:text-red-600 transition-colors shrink-0" data-testid={`deactivate-ann-${a.id}`}>
                      Deactivate
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Past Announcements */}
            {announcements.filter(a => !a.is_active).length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider mb-2">Deactivated</h3>
                {announcements.filter(a => !a.is_active).map(a => (
                  <div key={a.id} className="border border-[#E2E8F0] rounded-lg p-3 mb-2 bg-white opacity-50">
                    <div className="font-medium text-xs text-[#64748B]">{a.title}</div>
                    <div className="text-[10px] text-[#94A3B8]">{a.message}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Broadcast Section */}
            <div className="bg-white border border-[#E2E8F0] rounded-lg p-5">
              <h3 className="font-heading font-semibold text-sm text-[#0A1128] mb-4">In-App Notification Broadcast</h3>
              <p className="text-xs text-[#64748B] mb-4">Send a notification to every user's notification bell — good for feature announcements, important updates.</p>
              <div className="space-y-3">
                <input type="text" value={broadcastMsg.title} onChange={e => setBroadcastMsg(p => ({ ...p, title: e.target.value }))} placeholder="Notification title..." className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
                <textarea value={broadcastMsg.message} onChange={e => setBroadcastMsg(p => ({ ...p, message: e.target.value }))} rows={3} placeholder="Notification message..." className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] resize-none" />
                <button onClick={sendBroadcast} className="bg-[#F5F8F5] text-[#1B2B22] font-semibold px-5 py-2.5 rounded text-sm hover:bg-[#1A2238] transition-colors" data-testid="send-broadcast-btn">
                  Send to All Users
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "security" && (
          <div className="max-w-lg">
            <div className="bg-white border border-[#E2E8F0] rounded-lg p-5">
              <h3 className="font-heading font-semibold text-sm text-[#0A1128] mb-1">Super Admin Login</h3>
              <p className="text-xs text-[#64748B] mb-4">Change your login email and/or password. Once you save here, these credentials are <strong>locked</strong> and will persist across server restarts (the environment variable no longer overrides them).</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1">Current Password *</label>
                  <input type="password" value={credForm.current_password} onChange={e => setCredForm(p => ({ ...p, current_password: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" data-testid="cred-current-password" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1">New Email (login ID)</label>
                  <input type="email" value={credForm.new_email} onChange={e => setCredForm(p => ({ ...p, new_email: e.target.value }))} placeholder="leave blank to keep current" className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" data-testid="cred-new-email" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1">New Password</label>
                  <input type="password" value={credForm.new_password} onChange={e => setCredForm(p => ({ ...p, new_password: e.target.value }))} placeholder="min 8 chars — leave blank to keep current" className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" data-testid="cred-new-password" />
                </div>
                <button onClick={changeCredentials} disabled={credSaving} className="bg-[#1B2B22] text-white font-semibold px-5 py-2.5 rounded text-sm hover:bg-[#2F7D52] transition-colors disabled:opacity-50" data-testid="save-credentials-btn">
                  {credSaving ? "Saving…" : "Update & Lock Credentials"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Extend Trial Modal */}
      <Dialog open={!!extendModal} onOpenChange={() => setExtendModal(null)}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle className="font-heading font-bold text-[#0A1128]">Extend Trial</DialogTitle></DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-[#64748B]">Extend trial for <strong>{extendModal?.name}</strong></p>
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-2">Add Days</label>
              <div className="flex gap-2 mb-2">
                {[7, 14, 30, 60].map(d => (
                  <button key={d} onClick={() => { setExtendDays(d); setExtendDate(""); }} className={`flex-1 py-2 rounded text-sm font-semibold border transition-colors ${!extendDate && extendDays === d ? "bg-[#FFB703] border-[#FFB703] text-[#0A1128]" : "border-[#E2E8F0] text-[#64748B] hover:border-[#FFB703]"}`}>+{d}d</button>
                ))}
              </div>
              <input type="number" value={extendDays} onChange={e => { setExtendDays(parseInt(e.target.value) || 7); setExtendDate(""); }} min={1} max={730} disabled={!!extendDate} className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] disabled:opacity-40" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-2">Or set exact end date</label>
              <input type="date" value={extendDate} onChange={e => setExtendDate(e.target.value)} className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" data-testid="extend-trial-date" />
              {extendDate && <p className="text-[11px] text-[#2F7D52] mt-1">Trial will end on {new Date(extendDate).toLocaleDateString("en-IN")}</p>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setExtendModal(null)} className="flex-1 border border-[#E2E8F0] rounded py-2 text-sm text-[#64748B] hover:bg-slate-50">Cancel</button>
              <button onClick={extendTrial} className="flex-1 bg-[#FFB703] text-[#0A1128] font-semibold rounded py-2 text-sm hover:bg-[#E8A200]" data-testid="confirm-extend-trial">Extend</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Permanent delete — requires typing the company name */}
      <Dialog open={!!deleteModal} onOpenChange={() => { setDeleteModal(null); setDeleteTyped(""); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading font-bold text-red-700">Delete workspace permanently</DialogTitle></DialogHeader>
          <div className="py-2 space-y-4">
            <p className="text-sm text-[#64748B]">This removes <strong>{deleteModal?.name}</strong> with every user, project, invoice, bill and document in it. There is no undo and no backup is taken.</p>
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Type the company name to confirm</label>
              <input value={deleteTyped} onChange={e => setDeleteTyped(e.target.value)} placeholder={deleteModal?.name} autoComplete="off" className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-400" data-testid="delete-company-typed" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setDeleteModal(null); setDeleteTyped(""); }} className="flex-1 border border-[#E2E8F0] rounded py-2 text-sm text-[#64748B] hover:bg-slate-50">Cancel</button>
              <button onClick={deleteWorkspace} disabled={deleting || deleteTyped.trim() !== (deleteModal?.name || "").trim()} className="flex-1 bg-red-600 text-white font-semibold rounded py-2 text-sm hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="confirm-delete-company">Delete permanently</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Announcement Modal */}
      <Dialog open={showAnnForm} onOpenChange={setShowAnnForm}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading font-bold text-[#0A1128]">New Announcement Banner</DialogTitle></DialogHeader>
          <form onSubmit={createAnnouncement} className="space-y-4 py-2">
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Type</label>
              <div className="flex gap-2">
                {Object.entries(ANN_TYPES).map(([k, v]) => (
                  <button key={k} type="button" onClick={() => setAnnForm(p => ({ ...p, type: k }))}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded border text-xs font-semibold transition-colors ${annForm.type === k ? `${v.color} border-current` : "border-[#E2E8F0] text-[#64748B]"}`}>
                    <v.icon size={12} />{v.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Title *</label>
              <input type="text" required value={annForm.title} onChange={e => setAnnForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Scheduled Maintenance on Sunday" className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" data-testid="ann-title" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Message *</label>
              <textarea required value={annForm.message} onChange={e => setAnnForm(p => ({ ...p, message: e.target.value }))} rows={2} placeholder="Details of the announcement..." className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] resize-none" data-testid="ann-message" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Expires On (optional)</label>
              <input type="date" value={annForm.expires_at} onChange={e => setAnnForm(p => ({ ...p, expires_at: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowAnnForm(false)} className="px-4 py-2 border border-[#E2E8F0] rounded text-sm text-[#64748B] hover:bg-slate-50">Cancel</button>
              <button type="submit" className="bg-[#FFB703] text-[#0A1128] font-semibold px-5 py-2 rounded text-sm hover:bg-[#E8A200] transition-colors" data-testid="publish-ann-btn">Publish</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Force Reset Credentials */}
      {resetCreds && (
        <Dialog open onOpenChange={() => setResetCreds(null)}>
          <DialogContent className="max-w-xs">
            <DialogHeader><DialogTitle className="font-heading font-bold text-[#0A1128]">Password Reset</DialogTitle></DialogHeader>
            <div className="py-4 space-y-3">
              <p className="text-sm text-[#64748B]"><strong>{resetCreds.name}</strong> ({resetCreds.email})</p>
              <div className="bg-[#FEF3C7] border border-[#FFB703]/30 rounded p-3">
                <div className="text-xs text-[#64748B] mb-1">New Temporary Password</div>
                <div className="font-mono text-sm font-bold text-[#0A1128]">{resetCreds.temp_password}</div>
              </div>
            </div>
            <button onClick={() => setResetCreds(null)} className="w-full bg-[#FFB703] text-[#0A1128] font-semibold py-2.5 rounded text-sm hover:bg-[#E8A200]">Done</button>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
